import {
  Alert,
  Badge,
  Card,
  DescriptionList,
  Inline,
  SettingsSection,
  Stack,
  Text,
  type DescriptionListItem,
} from '@hkdevs/design-system';
import type { ReactNode } from 'react';
import type { AdminSettings } from '../../api/queries/settings';

type Partner = AdminSettings['partners'][number];

function linkAuth(partner: Partner): ReactNode {
  switch (partner.linkAuth) {
    case 'hs256Secret':
      return 'Shared secret (HS256)';
    case 'publicKeyPem':
      return (
        <Stack gap={1}>
          <span>Public key ({partner.linkAlgorithm})</span>
          <Text size="meta" tone="muted" mono>
            {partner.keyFingerprint}
          </Text>
        </Stack>
      );
    case 'jwksUrl':
      return (
        <Stack gap={1}>
          <span>Published keys ({partner.linkAlgorithm})</span>
          <Text size="meta" tone="muted">
            {partner.jwksUrl}
          </Text>
        </Stack>
      );
    default:
      return 'Off';
  }
}

const people = (count: number) =>
  count === 1 ? '1 enabled user has' : `${count} enabled users have`;

const users = (count: number) => (count === 1 ? '1 user' : `${count} users`);

function matchedBy(partner: Partner): ReactNode {
  if (partner.externalIdType === null) {
    return (
      <Stack gap={1}>
        <span>Email only</span>
        <Text size="meta" tone="muted">
          No external ID type
        </Text>
      </Stack>
    );
  }
  return (
    <Stack gap={1}>
      <span>{partner.externalIdType}</span>
      <Text size="meta" tone={partner.externalIdTypeExists ? 'muted' : 'danger'}>
        {partner.externalIdTypeExists
          ? `${people(partner.usersWithExternalId)} one`
          : 'No external ID type has this name'}
      </Text>
    </Stack>
  );
}

function byEmail(partner: Partner): ReactNode {
  if (!partner.matchByEmail) return 'Off';
  return (
    <Stack gap={1}>
      <span>{partner.emailDomains.map((domain) => `@${domain}`).join(', ')}</span>
      <Text size="meta" tone="muted">
        {partner.bindOnEmailMatch
          ? `The first sign-in records its ID as their ${partner.externalIdType}`
          : 'Its IDs are noted, not recorded'}
      </Text>
    </Stack>
  );
}

function idsSeen({ seenIds }: Partner): ReactNode {
  if (seenIds.users === 0) return 'None yet';
  const problems = [
    seenIds.usersChanged > 0 && `${users(seenIds.usersChanged)} sent more than one`,
    seenIds.valuesShared > 0 &&
      `${seenIds.valuesShared === 1 ? '1 ID' : `${seenIds.valuesShared} IDs`} sent for more than one user`,
  ].filter(Boolean);
  return (
    <Stack gap={1}>
      <span>For {users(seenIds.users)}</span>
      <Text size="meta" tone={problems.length > 0 ? 'danger' : 'muted'}>
        {problems.length > 0 ? problems.join('; ') : 'Each user’s ID has stayed the same'}
      </Text>
    </Stack>
  );
}

/**
 * The deployment's partner sites (ADR 0031), read-only, as the Settings screen story shows
 * them: how each signs users in (a key only as its fingerprint), the external ID type its
 * users are matched by, and a warning when that type doesn't exist. A partner that
 * matches by email (A23) shows its domains, whether it binds, whether pay viewers can be
 * signed in, and what the IDs it sent say about binding on them.
 */
export function PartnersSection({ partners }: { partners: AdminSettings['partners'] }) {
  return (
    <SettingsSection
      title="Partners"
      description="Trusted sites that sign their users in. They’re set up with the deployment: adding or changing one is a redeploy."
      hint="Managed by your deployment"
    >
      <Stack gap={4}>
        {partners.map((partner) => {
          const misconfigured = partner.externalIdType !== null && !partner.externalIdTypeExists;
          const items: DescriptionListItem[] = [
            { term: 'Login links', description: linkAuth(partner) },
            ...(partner.legacyEndpoint ? [{ term: 'Legacy endpoint', description: 'On' }] : []),
            { term: 'OIDC sign-in', description: partner.oidcIssuer ?? 'Off' },
            { term: 'Matched by', description: matchedBy(partner) },
            { term: 'By email', description: byEmail(partner) },
            ...(partner.matchByEmail || partner.seenIds.users > 0
              ? [{ term: 'IDs it sent', description: idsSeen(partner) }]
              : []),
            {
              term: 'Pay viewers',
              description: partner.allowPayViewers ? 'Can be signed in' : 'Refused',
            },
            { term: 'On Login', description: partner.showOnLogin ? 'A sign-in button' : 'No' },
          ];
          return (
            <Card key={partner.id}>
              <Stack gap={3}>
                <Inline gap={2}>
                  <Text weight="semibold">{partner.name}</Text>
                  <Text size="meta" tone="muted" mono>
                    {partner.id}
                  </Text>
                  {misconfigured && <Badge tone="danger">Misconfigured</Badge>}
                </Inline>
                {misconfigured && (
                  <Alert tone="warning" title={`${partner.name} can’t sign anyone in`}>
                    Its users are matched by “{partner.externalIdType}”, and there’s no such
                    external ID type. Add it below, then give users their {partner.name} IDs.
                  </Alert>
                )}
                <DescriptionList items={items} />
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </SettingsSection>
  );
}
